/**
 * Licensed to the Apache Software Foundation (ASF) under one
 * or more contributor license agreements.  See the NOTICE file
 * distributed with this work for additional information
 * regarding copyright ownership.  The ASF licenses this file
 * to you under the Apache License, Version 2.0 (the
 * "License"); you may not use this file except in compliance
 * with the License.  You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing,
 * software distributed under the License is distributed on an
 * "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
 * KIND, either express or implied.  See the License for the
 * specific language governing permissions and limitations
 * under the License.
 */
import {
  AdhocFilter,
  isFreeFormAdhocFilter,
  isSimpleAdhocFilter,
  SimpleAdhocFilter,
  CohortAdhocFilter,
} from '@superset-ui/core';
import {
  OPERATOR_ENUM_TO_OPERATOR_TYPE,
  Operators,
} from 'src/explore/constants';
import {
  getCohortLable,
  getCohortSQLExpression,
  getSimpleSQLExpression,
} from 'src/explore/exploreUtils';
import { Datasource } from 'src/explore/types';
import { Backend } from 'src/types/Database';

export const EXPRESSION_TYPES = {
  SIMPLE: 'SIMPLE',
  SQL: 'SQL',
};

export const OPERATORS_TO_SQL: any = {
  '==': '=',
  '!=': '<>',
  '>': '>',
  '<': '<',
  '>=': '>=',
  '<=': '<=',
  IN: 'IN',
  'NOT IN': 'NOT IN',
  CONTAINS_SENSITIVE: 'LIKE',
  CONTAINS: 'ILIKE',
  NOT_CONTAINS: 'NOT ILIKE',
  NOT_CONTAINS_SENSITIVE: 'NOT LIKE',
  STARTS_WITH: 'ILIKE',
  NOT_STARTS_WITH: 'NOT ILIKE',
  ENDS_WITH: 'ILIKE',
  NOT_ENDS_WITH: 'NOT ILIKE',
  LIKE: 'LIKE',
  ILIKE: 'ILIKE',
  REGEX: 'REGEX',
  'IS NOT NULL': 'IS NOT NULL',
  'IS NULL': 'IS NULL',
  'IS TRUE': 'IS TRUE',
  'IS FALSE': 'IS FALSE',
  'IS EMPTY': "= ''",
  'IS NOT EMPTY': "<> ''",
  // For boolean columns, the null value was converted to -1
  'IS BOOLEAN EMPTY': '= -1',
  'IS BOOLEAN NOT EMPTY': '<> -1',
  'LATEST PARTITION': ({ datasource }: { datasource: Datasource }) =>
    `= '{{ presto.latest_partition('${datasource.schema}.${datasource.datasource_name}') }}'`,
};

const OPERATORS_TO_KQL = {
  ...OPERATORS_TO_SQL,
  '==': '==',
  NOT_EQUALS: '<>',
  GREATER_THAN: '>',
  LESS_THAN: '<',
  GREATER_THAN_OR_EQUAL: '>=',
  LESS_THAN_OR_EQUAL: '<=',
  EQUALS: '==',
  IN: 'in',
  'NOT IN': '!in',
  NOT_IN: '!in',
  CONTAINS_SENSITIVE: 'contains_cs',
  CONTAINS: 'contains',
  NOT_CONTAINS: '!contains',
  NOT_CONTAINS_SENSITIVE: '!contains_cs',
  STARTS_WITH: 'startswith',
  NOT_STARTS_WITH: '!startswith',
  ENDS_WITH: 'endswith',
  NOT_ENDS_WITH: '!endswith',
  LIKE: 'contains_cs',
  ILIKE: 'contains',
  'IS NULL': 'isnull',
  'IS NOT NULL': 'isnotnull',
  'IS EMPTY': '==',
  'IS NOT EMPTY': '<>',
  IS_EMPTY: '==',
  IS_NOT_EMPTY: '<>',
};

export const translateToSql = (
  adhocFilter: AdhocFilter & { backend?: Backend },
  { useSimple }: { useSimple: boolean } = { useSimple: false },
  // showColName: Display the saved column name (true) or detail SQLs(false)
  showColName = false,
) => {
  const useKQL = adhocFilter.backend === 'kustokql';
  const OPERATOR_MAP = useKQL ? OPERATORS_TO_KQL : OPERATORS_TO_SQL;

  // Get cohot filter sql expression
  if (adhocFilter?.currentEditTab === 'COHORT') {
    // Get the cohort filter SQL expression
    const {
      cohort,
      cohortName,
      cohortOperator,
      cohortComparator,
      cohortTopSeries,
      cohortAggregator,
      cohortAggregatorColumn,
      cohortAggregatorOperator,
      cohortAggregatorComparator,
    } = adhocFilter as CohortAdhocFilter;

    // Show the cohort filter name
    if (showColName) {
      // For Cohort, return the simple label "{cohort_name} {operator} {value}""
      return getCohortLable(
        cohortName,
        OPERATOR_MAP[cohortOperator],
        cohortComparator,
        cohortAggregatorOperator
          ? OPERATOR_MAP[cohortAggregatorOperator]
          : undefined,
        cohortAggregatorComparator,
      );
    }

    // Get the cohort filter SQL expression
    if (cohort) {
      const cohortSql = getCohortSQLExpression(
        cohort,
        cohortOperator,
        OPERATOR_MAP[cohortOperator],
        cohortTopSeries,
        cohortComparator,
        cohortAggregator,
        cohortAggregatorColumn,
        cohortAggregatorOperator,
        cohortAggregatorComparator,
        useKQL,
      );
      // If user custom sql is same as the cohort sql after triming the sqlExpression, return the custom sql.
      if (cohortSql?.trim() === adhocFilter?.sqlExpression?.trim()) {
        return adhocFilter?.sqlExpression;
      }
      return cohortSql;
    }
  }
  if (isSimpleAdhocFilter(adhocFilter) || useSimple) {
    let { subject, operator } = adhocFilter as SimpleAdhocFilter;
    const comparator =
      'comparator' in adhocFilter ? adhocFilter.comparator : undefined;
    const column = 'column' in adhocFilter ? adhocFilter.column : undefined;
    let columnType;
    // Only saved columns have expressions
    if (column && (column as any).expression && !showColName) {
      subject = (column as any).expression;
    }
    if (column && (column as any).type) {
      columnType = (column as any).type;
    }

    operator =
      operator &&
      // 'LATEST PARTITION' supported callback only
      operator ===
        OPERATOR_ENUM_TO_OPERATOR_TYPE[Operators.LatestPartition].operation
        ? OPERATOR_MAP[operator](adhocFilter)
        : OPERATOR_MAP[operator];
    const originOperator = (adhocFilter as SimpleAdhocFilter).operator;

    if (
      originOperator === 'CONTAINS' ||
      originOperator === 'CONTAINS_SENSITIVE'
    ) {
      return getSimpleSQLExpression(
        subject,
        columnType,
        operator,
        comparator,
        '%',
        '%',
        useKQL,
      );
    }
    if (
      originOperator === 'NOT_CONTAINS' ||
      originOperator === 'NOT_CONTAINS_SENSITIVE'
    ) {
      return getSimpleSQLExpression(
        subject,
        columnType,
        operator,
        comparator,
        '%',
        '%',
        useKQL,
      );
    }
    if (
      originOperator === 'STARTS_WITH' ||
      originOperator === 'NOT_STARTS_WITH'
    ) {
      return getSimpleSQLExpression(
        subject,
        columnType,
        operator,
        comparator,
        '',
        '%',
        useKQL,
      );
    }
    if (originOperator === 'ENDS_WITH' || originOperator === 'NOT_ENDS_WITH') {
      return getSimpleSQLExpression(
        subject,
        columnType,
        operator,
        comparator,
        '%',
        '',
        useKQL,
      );
    }
    // Override the default behavior for UUID columns when using IS EMPTY or IS NOT EMPTY
    if (
      columnType === 'UUID' &&
      (originOperator === 'IS EMPTY' || originOperator === 'IS NOT EMPTY')
    ) {
      let uuidOperator = operator;
      const uuidComparator = '00000000-0000-0000-0000-000000000000'; // Empty UUID
      if (originOperator === 'IS EMPTY') {
        uuidOperator = '==';
      }
      if (originOperator === 'IS NOT EMPTY') {
        uuidOperator = '!=';
      }
      return getSimpleSQLExpression(
        subject,
        columnType,
        OPERATOR_MAP[uuidOperator],
        uuidComparator,
        undefined,
        undefined,
        useKQL,
      );
    }

    if (operator === undefined) {
      return getSimpleSQLExpression(
        subject,
        columnType,
        originOperator,
        comparator,
        undefined,
        undefined,
        useKQL,
      );
    }
    return getSimpleSQLExpression(
      subject,
      columnType,
      operator,
      comparator,
      undefined,
      undefined,
      useKQL,
    );
  }
  if (isFreeFormAdhocFilter(adhocFilter)) {
    return adhocFilter.sqlExpression;
  }
  return '';
};
